import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, AlertTriangle, Check, ChevronDown, History, Loader2, Lock,
  RotateCcw, Save, ShieldCheck, X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ApiError, api } from '@/lib/api';

/**
 * RBACMatrix — Slice 2 editable Tenant-Admin permission matrix.
 *
 * State model (flat for cheap selectors / minimal rerenders):
 *   serverMatrix  Map<"module.action", "allow" | "deny" | "inherit">
 *   draft         same shape, mutated locally on cell click
 *   dirtyKeys     Set<"module.action">  — keys whose draft != server
 *
 * Save sends only the diff (dirtyKeys → {module, action, to}). Backend
 * rails enforce optimistic concurrency, privilege escalation, immutable
 * roles, and last-admin lock. Each rail returns a structured 4xx with a
 * `code_hint` we surface as a friendly inline message below.
 */
export function RBACMatrix() {
  const qc = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  // Roles for the role-selector dropdown
  const rolesQ = useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: () => api.get('/admin/rbac/roles'),
  });

  // Auto-select the first non-immutable role on first load
  useEffect(() => {
    if (selectedRoleId || !rolesQ.data?.length) return;
    const firstEditable = rolesQ.data.find((r) => r.name !== 'Super Admin');
    setSelectedRoleId((firstEditable ?? rolesQ.data[0]).id);
  }, [rolesQ.data, selectedRoleId]);

  // Matrix for the selected role
  const matrixQ = useQuery({
    queryKey: ['rbac', 'matrix', selectedRoleId],
    queryFn: () => api.get(`/admin/rbac/matrix?role_id=${selectedRoleId}`),
    enabled: !!selectedRoleId,
  });

  // Draft state — rebuilt whenever the server payload changes
  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!matrixQ.data) return;
    setDraft(structuredClone(matrixQ.data.matrix));
    setReason('');
    setServerError(null);
  }, [matrixQ.data]);

  // Compute dirty keys lazily
  const dirtyKeys = useMemo(() => {
    if (!matrixQ.data) return new Set();
    const dirty = new Set();
    for (const [mKey, row] of Object.entries(draft)) {
      const serverRow = matrixQ.data.matrix[mKey] ?? {};
      for (const [aKey, val] of Object.entries(row)) {
        if (serverRow[aKey] !== val) dirty.add(`${mKey}.${aKey}`);
      }
    }
    return dirty;
  }, [draft, matrixQ.data]);

  const role = matrixQ.data?.role;
  const isImmutable = role?.name === 'Super Admin';

  // Save mutation
  const saveMut = useMutation({
    mutationFn: (body) => api.put(`/admin/rbac/matrix/${selectedRoleId}`, body),
    onSuccess: () => {
      // Refetch the matrix → triggers the useEffect above to reset draft + dirty
      qc.invalidateQueries({ queryKey: ['rbac', 'matrix', selectedRoleId] });
      qc.invalidateQueries({ queryKey: ['rbac', 'roles'] });
      setServerError(null);
    },
    onError: (err) => {
      const apiErr = err instanceof ApiError ? err : null;
      const details = (apiErr?.details ?? {});
      setServerError({
        code: details.code_hint ?? `HTTP_${apiErr?.status ?? 'ERR'}`,
        message: apiErr?.message ?? 'Save failed.',
        details,
      });
    },
  });

  const onCellClick = (moduleKey, actionKey) => {
    if (isImmutable) return;
    setDraft((prev) => {
      const row = { ...(prev[moduleKey] ?? {}) };
      // Slice 2 is binary allow ↔ deny per cell. The "inherit" tri-state
      // ships with Slice 3 once parent_role_id is populated.
      row[actionKey] = row[actionKey] === 'allow' ? 'deny' : 'allow';
      return { ...prev, [moduleKey]: row };
    });
  };

  const onRevert = () => {
    if (matrixQ.data) setDraft(structuredClone(matrixQ.data.matrix));
    setServerError(null);
  };

  const onSave = () => {
    if (!matrixQ.data || dirtyKeys.size === 0) return;
    const changes = [...dirtyKeys].map((key) => {
      const lastDot = key.lastIndexOf('.');
      const mKey = key.substring(0, lastDot);
      const aKey = key.substring(lastDot + 1);
      return { module: mKey, action: aKey, to: draft[mKey][aKey] };
    });
    saveMut.mutate({
      etag: matrixQ.data.etag,
      changes,
      reason: reason.trim() || null,
    });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {/* ---- Header ---- */}
      <div className="flex flex-wrap items-start gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[16rem]">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Role Permissions Matrix
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Pick a role, then click any capability to toggle it. Changes are queued locally — nothing saves until you click <strong>Save changes</strong>.
          </p>
        </div>
        <RoleSelector
          roles={rolesQ.data ?? []}
          loading={rolesQ.isLoading}
          value={selectedRoleId}
          onChange={setSelectedRoleId}
        />
      </div>

      {/* ---- Selected role meta ---- */}
      {role && (
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50/60 px-5 py-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/30">
          <span className="font-semibold text-slate-700 dark:text-slate-200">{role.name}</span>
          <span className="text-slate-500">{role.user_count} user{role.user_count === 1 ? '' : 's'}</span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-500">etag v{matrixQ.data.etag}</span>
          {isImmutable && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-tag text-rose-700 ring-1 ring-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-800">
              <Lock size={10} />
              Immutable — read-only
            </span>
          )}
          {dirtyKeys.size > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-tag text-amber-800 ring-1 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800">
              <History size={10} />
              {dirtyKeys.size} unsaved change{dirtyKeys.size === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {/* ---- Read-only role notice (e.g. Super Admin is immutable) ---- */}
      {role && isImmutable && (
        <div className="m-5 flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900 dark:border-indigo-900/60 dark:bg-indigo-950/30 dark:text-indigo-200">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">“{role.name}” is read-only — it can’t be edited</div>
            <div className="text-xs opacity-90">
              This is the platform-owner role: it always keeps every permission, on
              purpose, so the system can never be accidentally locked out. To change
              permissions, pick a <strong>different role</strong> from the dropdown at
              the top-right (e.g. Tenant Admin, Service Desk Manager, Service Desk
              Agent…) — its toggles become editable and a <strong>Save changes</strong>{' '}
              bar appears.
            </div>
          </div>
        </div>
      )}

      {/* ---- Loading / error ---- */}
      {matrixQ.isLoading && (
        <div className="flex items-center justify-center px-5 py-10 text-sm text-slate-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading matrix…
        </div>
      )}
      {matrixQ.isError && (
        <div className="m-5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{(matrixQ.error)?.message ?? 'Failed to load matrix.'}</span>
        </div>
      )}

      {/* ---- The grid ---- */}
      {matrixQ.data && (
        <MatrixGrid
          matrixData={matrixQ.data}
          draft={draft}
          dirtyKeys={dirtyKeys}
          isImmutable={isImmutable}
          onCellClick={onCellClick}
        />
      )}

      {/* ---- Server-side error surfacing (rails) ---- */}
      {serverError && (
        <RailErrorBanner error={serverError} onReload={() => {
          qc.invalidateQueries({ queryKey: ['rbac', 'matrix', selectedRoleId] });
          setServerError(null);
        }} />
      )}

      {/* ---- Save bar ---- */}
      {matrixQ.data && !isImmutable && (
        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional but recommended for high-risk changes)…"
            maxLength={2000}
            className="flex-1 min-w-[12rem] rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
          <button
            type="button"
            onClick={onRevert}
            disabled={dirtyKeys.size === 0 || saveMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <RotateCcw size={12} />
            Revert
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={dirtyKeys.size === 0 || saveMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saveMut.isPending ? 'Saving…' : `Save changes${dirtyKeys.size > 0 ? ` (${dirtyKeys.size})` : ''}`}
          </button>
        </div>
      )}

      {/* ---- Footer legend ---- */}
      <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-5 py-3 text-[11px] text-slate-500 dark:border-slate-800 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <Cell value="allow" dangerous={false} />
          Allowed
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Cell value="allow" dangerous={true} />
          Allowed · high-risk
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Cell value="deny" dangerous={false} />
          Denied
        </span>
        <span className="text-slate-400 dark:text-slate-500">
          Each module lists only the capabilities that apply to it.
        </span>
      </div>
    </div>
  );
}

// ---------- Sub-components ------------------------------------------------

function RoleSelector({ roles, loading, value, onChange }) {
  return (
    <label className="relative inline-flex items-center">
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading || !roles.length}
        className="appearance-none rounded-md border border-slate-200 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
      >
        {!value && <option value="">Select role…</option>}
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name} ({r.user_count} user{r.user_count === 1 ? '' : 's'})
          </option>
        ))}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute right-2 text-slate-400" />
    </label>
  );
}

function MatrixGrid({ matrixData, draft, dirtyKeys, isImmutable, onCellClick }) {
  const { modules, actions, matrix: serverMatrix } = matrixData;
  const [filter, setFilter] = useState('');

  // Action lookup by key — each module shows ONLY its own applicable actions
  // (those present in serverMatrix), grouped per-module (Freshservice pattern)
  // instead of one wide sparse grid. Toggle / dirty / save logic is unchanged.
  const actionByKey = useMemo(() => {
    const m = {};
    for (const a of actions) m[a.key] = a;
    return m;
  }, [actions]);

  const sortedModules = useMemo(
    () => [...modules].sort((a, b) => a.display_order - b.display_order),
    [modules],
  );

  const q = filter.trim().toLowerCase();
  const visibleModules = useMemo(
    () =>
      q
        ? sortedModules.filter(
            (m) =>
              m.display_name.toLowerCase().includes(q) ||
              (m.description ?? '').toLowerCase().includes(q),
          )
        : sortedModules,
    [sortedModules, q],
  );

  return (
    <div className="px-5 py-4">
      {/* Filter — 40+ modules need a quick find. Pure display; no data effect. */}
      <div className="mb-3 flex items-center gap-2">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter modules…"
          className="w-full max-w-xs rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        />
        <span className="text-[11px] text-slate-400">
          {visibleModules.length} module{visibleModules.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="space-y-2.5">
        {visibleModules.map((mod) => {
          const serverRow = serverMatrix?.[mod.key] ?? {};
          const caps = Object.keys(serverRow)
            .map((k) => actionByKey[k])
            .filter(Boolean)
            .sort((a, b) => a.display_order - b.display_order);
          if (caps.length === 0) return null;
          const grantedCount = caps.filter((a) => draft?.[mod.key]?.[a.key] === 'allow').length;
          const moduleDirty = caps.some((a) => dirtyKeys.has(`${mod.key}.${a.key}`));
          return (
            <div
              key={mod.id}
              className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-[12rem] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-slate-100">{mod.display_name}</span>
                    {moduleDirty && (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-amber-500"
                        title="Unsaved changes in this module"
                      />
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {mod.description}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {grantedCount}/{caps.length} allowed
                </span>
              </div>

              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {caps.map((a) => {
                  const draftVal = draft?.[mod.key]?.[a.key];
                  const serverVal = serverRow[a.key];
                  const dirty = dirtyKeys.has(`${mod.key}.${a.key}`);
                  const dangerous = a.risk_level === 'critical' || a.risk_level === 'high';
                  const on = draftVal === 'allow';
                  const tone = on
                    ? dangerous
                      ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300'
                      : 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : 'border-slate-200 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400';
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onCellClick(mod.key, a.key)}
                      disabled={isImmutable}
                      title={
                        dirty
                          ? `Pending change: ${serverVal} → ${draftVal}`
                          : `${a.display_name} on ${mod.display_name}: ${draftVal}`
                      }
                      aria-label={`${a.display_name} on ${mod.display_name}: ${draftVal}`}
                      className={`relative inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition ${tone} ${
                        isImmutable
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:ring-2 hover:ring-indigo-300 dark:hover:ring-indigo-700'
                      }`}
                    >
                      <Cell value={draftVal} dangerous={dangerous} />
                      <span>{a.display_name}</span>
                      {dangerous && on && <span className="text-[9px] opacity-70">high-risk</span>}
                      {dirty && (
                        <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-900" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {visibleModules.length === 0 && (
          <div className="py-6 text-center text-xs text-slate-400">No modules match “{filter}”.</div>
        )}
      </div>
    </div>
  );
}

function Cell({ value, dangerous }) {
  if (value === 'allow') {
    const tone = dangerous
      ? 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300'
      : 'border-emerald-300 bg-emerald-50 text-emerald-600 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
    return (
      <span className={`flex h-5 w-5 items-center justify-center rounded border ${tone}`}>
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  }
  // deny
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded border border-slate-200 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-600">
      <X className="h-3.5 w-3.5" strokeWidth={2.5} />
    </span>
  );
}

function RailErrorBanner({ error, onReload }) {
  // Translate the structured `code_hint` from each safety rail into a
  // friendly explanation + the right CTA for that specific failure mode.
  const map = {
    RBAC_STALE_ETAG: {
      icon: <History className="h-4 w-4" />,
      title: 'Another administrator changed this role',
      body: 'Your view is out of date. Reload the matrix and re-apply your changes.',
      cta: 'Reload',
      ctaAction: onReload,
    },
    RBAC_PRIVILEGE_ESCALATION: {
      icon: <AlertTriangle className="h-4 w-4" />,
      title: 'Cannot grant a permission you do not hold',
      body: error.details?.escalating
        ? `Permission(s) you don't hold: ${error.details.escalating.join(', ')}`
        : error.message,
    },
    RBAC_IMMUTABLE_ROLE: {
      icon: <Lock className="h-4 w-4" />,
      title: 'This role is immutable',
      body: error.message,
    },
    RBAC_LAST_ADMIN_LOCK: {
      icon: <AlertTriangle className="h-4 w-4" />,
      title: 'Last-admin lock — change refused',
      body: error.message,
    },
    RBAC_UNKNOWN_PERMISSION: {
      icon: <AlertCircle className="h-4 w-4" />,
      title: 'Unknown permission referenced',
      body: error.message,
    },
  };
  const info = map[error.code] ?? {
    icon: <AlertCircle className="h-4 w-4" />,
    title: 'Save failed',
    body: error.message,
  };

  return (
    <div className="m-5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
      <span className="mt-0.5 shrink-0">{info.icon}</span>
      <div className="flex-1">
        <div className="font-semibold">{info.title}</div>
        <div className="text-xs opacity-90">{info.body}</div>
      </div>
      {info.cta && (
        <button
          type="button"
          onClick={info.ctaAction}
          className="rounded-md bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700"
        >
          {info.cta}
        </button>
      )}
    </div>
  );
}
